/**
 * Argon2id, the one place a low-entropy secret is turned into a key.
 *
 * Everything else in this directory operates on 256-bit random values, where
 * the cost of a guess is irrelevant because guessing is hopeless. A password is
 * the exception: it is short, it is chosen by a human, and if the wrapped
 * master key ever leaks then the only thing between an attacker and the user's
 * files is how expensive each guess is. That makes the parameters below a
 * security control, not a tuning knob.
 *
 * Argon2id rather than PBKDF2 because it is memory-hard: PBKDF2 is trivially
 * parallelised on a GPU, and website-main is stuck on it only because Workers'
 * WebCrypto caps PBKDF2 at 100k iterations and offers nothing better. In the
 * browser there is no such constraint, so we use the algorithm that actually
 * resists the attack.
 *
 * The same function serves two callers with different salts and different
 * domain strings, and they must never collide:
 *
 *   · deriveAuthSecret — the value sent to mauvely.com in place of a password
 *   · deriveKek        — the key that unwraps the vault, which never leaves
 *
 * Same password, two salts, two unrelated outputs. That is what lets the
 * account system authenticate a user it cannot decrypt for.
 */

import { argon2id } from 'hash-wasm';
import { hkdfAesKey, type Bytes } from './primitives';

export interface KdfParams {
  alg: 'argon2id';
  /** KiB of memory. */
  m: number;
  /** Passes. */
  t: number;
  /** Lanes. */
  p: number;
  /** Bumped only if the meaning of the parameters changes, not their values. */
  v: number;
}

/**
 * OWASP's current Argon2id recommendation (64 MiB, t=3, p=1).
 *
 * Roughly half a second on a mid-range laptop and around a second on a phone,
 * which is the most a sign-in can spend without feeling broken. 64 MiB is also
 * chosen to stay clear of mobile Safari's memory ceiling — asking for 256 MiB
 * gets the tab killed rather than a slower hash.
 *
 * These travel with each user's row in `cloud_users.kdf_params`, so raising
 * them later applies to new and re-keyed vaults while existing ones keep
 * working under the parameters they were built with.
 */
export const DEFAULT_KDF_PARAMS: KdfParams = { alg: 'argon2id', m: 65536, t: 3, p: 1, v: 1 };

/** Guards against a server-supplied `kdf_params` being used to mount a denial
 *  of service — or, worse, to talk a client into a weak derivation. */
export function assertSaneParams(params: KdfParams): asserts params is KdfParams {
  const ok =
    params?.alg === 'argon2id' &&
    Number.isInteger(params.m) &&
    params.m >= 19456 && // OWASP's floor, 19 MiB
    params.m <= 262144 && // 256 MiB, past which mobile browsers fall over
    Number.isInteger(params.t) &&
    params.t >= 2 &&
    params.t <= 10 &&
    Number.isInteger(params.p) &&
    params.p >= 1 &&
    params.p <= 4;

  if (!ok) throw new Error('Refusing to derive a key with unrecognised KDF parameters.');
}

const KEY_BYTES = 32;

/** Raw Argon2id. Prefer the two named derivations below — they carry the domain
 *  separation that keeps the auth secret and the vault key apart. */
export async function argon2(
  password: string,
  salt: Uint8Array,
  params: KdfParams = DEFAULT_KDF_PARAMS
): Promise<Bytes> {
  assertSaneParams(params);
  const hash = await argon2id({
    password,
    salt,
    parallelism: params.p,
    iterations: params.t,
    memorySize: params.m,
    hashLength: KEY_BYTES,
    outputType: 'binary'
  });
  return hash as Bytes;
}

/**
 * The key-encryption key that unwraps the vault's master key.
 *
 * Derived from the password under `cloud_users.kek_salt` and then run through
 * HKDF with a Cloud-specific label, so even a salt reused by mistake could not
 * make this equal the authentication secret. Non-extractable: nothing needs
 * these bytes, only the ability to unwrap with them.
 */
export async function deriveKek(
  password: string,
  kekSalt: Uint8Array,
  params: KdfParams = DEFAULT_KDF_PARAMS
): Promise<CryptoKey> {
  return hkdfAesKey(await argon2(password, kekSalt, params), 'mauvely.cloud.kek.v1');
}

/**
 * What gets sent to mauvely.com in place of the password.
 *
 * The server stores a PBKDF2 hash of this string, exactly as it used to store
 * one of the password — so from the accounts database's point of view nothing
 * changed, while the password itself never crosses the network. See
 * website-main's `POST /api/auth/prelogin` for where the salt comes from.
 *
 * Returned as base64 because it travels as a JSON string.
 */
export async function deriveAuthSecret(
  password: string,
  authSalt: Uint8Array,
  params: KdfParams = DEFAULT_KDF_PARAMS
): Promise<Bytes> {
  return argon2(password, authSalt, params);
}
